// @ts-check
import { createContext, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { useIsAuthenticated, useAccount, useMsal } from '@azure/msal-react';
import { useQuery, useQueryClient, useMutation, useQueries } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import createMutationRequest from 'utils/createMutationRequest';
import { getConfig } from '../../config';
import API from '../API';
import _ from 'lodash';
import { clearUserData } from 'utils/cleardata';
import { orgKeys } from 'components/Admin/Organizations/queries';
import { userSettingsKey } from './queries';
import { privileges } from 'utils/types';
import { userKeys } from 'components/Admin/Organizations/Users/queries';
/**
 * @typedef {object} AuthUser
 * @prop {array} claims
 * @prop {string=} userId
 * @prop {string} objectId
 * @prop {string[]} emails
 * @prop {string} userName
 * @prop {string} fullName
 * @prop {string=} apiToken
*/

/**
 * @typedef {object} ExtendedUser
 * @extends AuthUser
 * @prop {string} currentOrg
 * @prop {boolean} isLoading
 * @prop {string[]} organizations
 * @prop {string} currentOrgs
 * @prop {string[]} effectiveOrgs,
 * @prop {object} settings
 * @prop {array} effectivePrivileges
 * @prop {function (array):boolean} hasPrivilege
 */

/**
 * @constant
 * @type {AuthUser}
 */
const defaultUserData = {
  claims: [],
  objectId: '',
  emails: [],
  fullName: '',
  userName: ''
};

const defaultUser = {
  displayName: '',
  email: '',
  id: ''
};

const defaultUserSettings = {
  theme: 'light',
  language: 'en',
  units: 'metric',
  orgFilter: [],
  licenseAccepted: true
};

const orgPrivilegeChecks = ({ subjectId, organizationId, privileges }) => {
  return _.map(privileges, privilege => {
    return {
      subjectType: 'user',
      subjectId,
      privilege,
      organizationId
    };
  });
};


/**
 * @typedef AuthServiceReturnType
 * @prop {ExtendedUser=} user
 * @prop {function} getUserData,
 * @prop {array} permissions,
 * @prop {function} updateUserData,
 * @prop {function} hasPrivilege
 * @prop {boolean} isLoggedIn,
 * @prop {*} api
 */

/**
 * @description Service to manage authentication and user data
 * @returns AuthServiceReturnType
 */
const useAuthService = () => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const isLoggedIn = useIsAuthenticated();
  const [userData, setUserData] = useState(defaultUserData);
  const [atsResponse, setAtsResponse] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!atsResponse && account && inProgress === InteractionStatus.None) {
      const request = {
        scopes: [getConfig().msalConfig.auth.clientId],
        account: account
      };
      instance.acquireTokenSilent(request).then((response) => {
        setAtsResponse(response);
        setUserData(old => {
          return { ...old,
            apiToken: response?.idToken,
            userId: account.localAccountId,
            emails: account.idTokenClaims.emails,
            fullName: account.name,
            userName: account.username
          };
        });
      }).catch((e) => {
        clearUserData();
        if (e instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect(request);
        }
      });
    }
  }, [inProgress, accounts, instance, atsResponse, account]);

  const api = useMemo(() => {
    return new API(
      {
        ...getConfig()
      });
  }, []);

  const updateUserSettings = useMutation(createMutationRequest(api.core.updateUserSettings), {
    onSuccess: useCallback(() => {
    }, []),
    onError: useCallback((error) => {
      console.error('Failed to update userSettings', error);
      queryClient.invalidateQueries({ queryKey: userSettingsKey.user(userData?.userId) });
    }, [queryClient, userData?.userId])
  });
  const userSettings = useQuery(userSettingsKey.user(userData?.userId), createQueryRequest(api.core.getUserSettings),
    { enabled: !!userData?.userId,
      initialData: defaultUserSettings
    }
  );

  const userQuery = useQuery({
    queryKey: userKeys.user(userData?.userId),
    queryFn: createQueryRequest(api.core.getUser),
    enabled: !!userData?.userId,
    initialData: defaultUser
  });

  const orgsQuery = useQuery(orgKeys.organizations(), createQueryRequest(api.core.getOrganizations), {
    enabled: !!userData?.userId,
    initialData: []
  });

  const orgSettingsQuery = useQueries({
    queries: _.map(orgsQuery.data, org => {
      return {
        queryKey: orgKeys.orgSettings(org?.id),
        queryFn: createQueryRequest(api.core.getOrganizationSettings),
        enabled: !!org?.id
        // staleTime: 24 * 60 * 60 * 1000
      };
    })
  });

  const privilegesQuery = useQueries({
    queries: _.map(orgsQuery?.data, org => {
      return {
        queryKey: orgKeys.privileges(orgPrivilegeChecks({ subjectId: userData?.userId, organizationId: org?.id, privileges: org?.privileges })),
        queryFn: createQueryRequest(api.core.checkPrivileges),
        enabled: !!userData?.userId && !!org?.id,
        initialData: []
      };
    })
  });

  const updateUserTimer = useRef(null);

  const updateUserData = useCallback((newData) => {
    const orgFilter = _.map(newData?.currentOrgs, 'id');
    const settings = { ...newData.settings, orgFilter, userId: newData.userId };
    queryClient.setQueryData(userSettingsKey.user(userData?.userId), settings);
    clearTimeout(updateUserTimer.current);
    updateUserTimer.current = setTimeout(() => {
      updateUserSettings.mutate(settings);
    }, 1000);
  }, [queryClient, userData?.userId, updateUserSettings]);

  const acceptLicense = useCallback(() => {
    const settings = { ...userSettings.data, licenseAccepted: true };
    updateUserData({ ...userData, settings });
  }, [updateUserData, userData, userSettings.data]);

  const userPrivileges = useMemo(() => {
    const up = _.map(privilegesQuery, org => ({ [_.first(org.data)?.organizationId]: _.reduce(org.data, (acc, data) => {
      if (data.result === true) {
        return _.concat(acc, data.privilege);
      }
    }, []) })
    );
    return _.assign({}, ...up);
  }, [privilegesQuery]);

  const privilegesQueryIsLoading = _.some(privilegesQuery, query => query.isLoading);
  const isLoading = orgsQuery.isLoading || privilegesQueryIsLoading || userSettings.isLoading;

  const organizations = useMemo(() => {
    return _.map(orgsQuery.data, (org, index) => {
      return {
        ...org,
        privileges: userPrivileges[org.id] ?? [],
        settings: orgSettingsQuery[index]?.data ?? {}
      };
    });
  }, [orgSettingsQuery, orgsQuery.data, userPrivileges]);

  const hasAcceptedLicense = useMemo(() => {
    const orgNeedLicenseAccept = _.some(organizations, org => {
      return org?.settings?.needLicenseAgreement === true;
    });
    return userSettings.data?.licenseAccepted ?? !orgNeedLicenseAccept;
    // return userSettings.data?.licenseAccepted ?? false;
  }, [organizations, userSettings.data?.licenseAccepted]);

  const user = useMemo(() => {
    const userOrgs = _.filter(organizations, (org) => {
      return _.includes(userSettings.data?.orgFilter, org.id);
    });
    const effectiveOrgs = _.isEmpty(userOrgs) ? organizations : userOrgs;
    const effectivePrivileges = _.reduce(effectiveOrgs, (acc, org) => {
      return _.union(acc, org.privileges);
    }, []);
    return {
      ...userData,
      fullName: userQuery.data?.displayName ?? userData.fullName,
      isLoading,
      organizations: organizations,
      currentOrgs: userOrgs,
      effectiveOrgs,
      settings: userSettings.data,
      effectivePrivileges,
      hasAcceptedLicense: hasAcceptedLicense,
      acceptLicense,
      hasPrivilege: (wantedPrivileges) => {
        if (_.isEmpty(effectivePrivileges)) {
          return false;
        }
        if (_.includes(wantedPrivileges, privileges.Any)) {
          return true;
        }
        return _.some(effectivePrivileges, effectivePrivilege =>
          _.includes(wantedPrivileges, effectivePrivilege));
      }
    };
  }, [organizations, userData, userQuery.data?.displayName, isLoading, userSettings.data, hasAcceptedLicense, acceptLicense]);

  const getUserData = useCallback(() => {
    return { ...user, isLoading };
  }, [isLoading, user]);

  const permissions = useMemo(() => {
    return _.uniq(_.flattenDeep(_.map(user?.currentOrgs, 'permissions'))) ?? [];
  }, [user?.currentOrgs]);

  const hasPrivilege = useCallback((privilege) => {
    console.error('hasPrivilege is deprecated, use user.hasPrivilege instead');
    return _.includes(user?.effectivePrivileges, privilege);
  }, [user?.effectivePrivileges]);

  return useMemo(() => ({
    user,
    getUserData,
    permissions,
    updateUserData,
    hasPrivilege,
    isLoggedIn,
    api
  }), [user, getUserData, permissions, updateUserData, hasPrivilege, isLoggedIn, api]);
};

const AuthService = createContext(null);

export { useAuthService, AuthService };
